import Phaser from 'phaser';
import { ROSTER, pickOpponent, type BeyDef } from '../game/stats.ts';
import { specialFor } from '../game/specials.ts';
import { newRun, type GameMode } from '../game/tournament.ts';
import { sound } from '../audio/sound.ts';
import { loadPrefs, savePrefs } from '../game/storage.ts';
import { ARENAS } from '../arenas.ts';
import { WORLD } from '../world.ts';
import { backdropGlow } from './TitleScene.ts';

const STAT_KEYS = ['attack', 'defense', 'stamina', 'weight', 'speed', 'durability'] as const;
const DIFFS: readonly { name: string; value: number }[] = [
  { name: 'Easy', value: 0.4 },
  { name: 'Normal', value: 0.7 },
  { name: 'Hard', value: 0.95 },
];

export class SelectScene extends Phaser.Scene {
  private index = 0;
  private mode: GameMode = 'tournament';
  private hard = false;
  private versusPlayer: 1 | 2 = 1;
  private p1Id = '';
  private cards: Phaser.GameObjects.Container[] = [];
  private faces: Phaser.GameObjects.Sprite[] = [];
  private statsG!: Phaser.GameObjects.Graphics;
  private statLabels: Phaser.GameObjects.Text[] = [];
  private statValues: Phaser.GameObjects.Text[] = [];
  private blurb!: Phaser.GameObjects.Text;
  private specialText!: Phaser.GameObjects.Text;
  private arenaIdx = 0;
  private arenaLabel!: Phaser.GameObjects.Text;
  private difficultyIdx = 1;
  private difficultyLabel!: Phaser.GameObjects.Text;

  constructor() {
    super('Select');
  }

  create(data: { mode?: GameMode; hard?: boolean; versusPlayer?: 1 | 2; p1Id?: string }): void {
    this.mode = data?.mode ?? 'tournament';
    this.hard = data?.hard ?? false;
    this.versusPlayer = data?.versusPlayer === 2 ? 2 : 1;
    this.p1Id = data?.p1Id ?? '';
    this.index = 0;
    this.cards = [];
    this.faces = [];
    this.statLabels = [];
    this.statValues = [];
    this.cameras.main.setBackgroundColor('#05060a');
    backdropGlow(this);

    this.add
      .text(
        WORLD.width / 2,
        26,
        this.mode === 'versus'
          ? `VERSUS — PLAYER ${this.versusPlayer}`
          : this.mode === 'tournament'
            ? this.hard
              ? "CHAMPION'S GAUNTLET"
              : 'TOURNAMENT'
            : 'EXHIBITION',
        {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: this.hard ? '#ff5b5b' : '#ffd23b',
        },
      )
      .setOrigin(0.5);

    this.add
      .text(WORLD.width / 2, 60, 'CHOOSE YOUR BEY', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '44px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#000',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    const spacing = 232;
    const startX = WORLD.width / 2 - ((ROSTER.length - 1) / 2) * spacing;
    ROSTER.forEach((def, i) => {
      const c = this.makeCard(def, startX + i * spacing, 250);
      this.cards.push(c);
    });

    this.buildStatPanel();

    this.blurb = this.add
      .text(WORLD.width / 2, 626, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '21px',
        color: '#ffd23b',
        align: 'center',
        wordWrap: { width: 760 },
      })
      .setOrigin(0.5, 0);

    this.specialText = this.add
      .text(WORLD.width / 2, 690, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '18px',
        color: '#5ad6ff',
        align: 'center',
        wordWrap: { width: 760 },
      })
      .setOrigin(0.5, 0);

    this.add
      .text(WORLD.width / 2, WORLD.height - 36, '◄ ►  select    ENTER  let it rip!    B  build    A  arena', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '21px',
        color: '#8aa0c8',
      })
      .setOrigin(0.5);

    // Arena chooser — default to the last-used arena (persisted).
    const prefs = loadPrefs();
    this.arenaIdx = Math.max(
      0,
      ARENAS.findIndex((a) => a.id === (this.registry.get('arena') ?? prefs.arena)),
    );
    this.registry.set('arena', ARENAS[this.arenaIdx].id);
    this.arenaLabel = this.add
      .text(WORLD.width / 2, 404, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '17px',
        color: '#5ad6ff',
      })
      .setOrigin(0.5);
    this.refreshArena();
    this.input.keyboard!.on('keydown-A', () => this.cycleArena());

    // Difficulty chooser (Exhibition only — tournament scales per stage,
    // versus is human-vs-human).
    if (this.mode === 'exhibition') {
      this.difficultyIdx = Math.min(DIFFS.length - 1, Math.max(0, prefs.difficultyIdx));
      this.difficultyLabel = this.add
        .text(WORLD.width / 2, 430, '', {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '17px',
          color: '#7CFC9A',
        })
        .setOrigin(0.5);
      this.refreshDifficulty();
      this.input.keyboard!.on('keydown-D', () => this.cycleDifficulty());
    }

    this.input.keyboard!.on('keydown-LEFT', () => this.move(-1));
    this.input.keyboard!.on('keydown-RIGHT', () => this.move(1));
    this.input.keyboard!.on('keydown-ENTER', () => this.confirm());
    // Build-your-own is available in solo modes (not the versus pick flow).
    if (this.mode !== 'versus') {
      this.input.keyboard!.on('keydown-B', () =>
        this.scene.start('Workshop', { mode: this.mode, hard: this.hard }),
      );
    }

    this.refresh();
  }

  private makeCard(def: BeyDef, x: number, y: number): Phaser.GameObjects.Container {
    const frame = this.add.graphics();
    frame.fillStyle(0x121a2b, 0.9);
    frame.fillRoundedRect(-102, -112, 204, 224, 14);
    const face = this.add.sprite(0, -34, `bey-${def.id}-face`).setDisplaySize(108, 108);
    this.faces.push(face);
    const name = this.add
      .text(0, 56, def.name, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '21px',
        fontStyle: 'bold',
        color: '#ffffff',
        align: 'center',
        wordWrap: { width: 196 },
      })
      .setOrigin(0.5);
    const spin = def.spinDir === 'left' ? '↺' : '↻';
    const type = this.add
      .text(0, 90, `${def.type.toUpperCase()}  ${spin}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '16px',
        color: Phaser.Display.Color.IntegerToColor(def.color).rgba,
      })
      .setOrigin(0.5);
    return this.add.container(x, y, [frame, face, name, type]);
  }

  private buildStatPanel(): void {
    const x = WORLD.width / 2 - 180;
    const top = 462;
    const rowH = 26;
    this.statsG = this.add.graphics();
    STAT_KEYS.forEach((k, i) => {
      const y = top + i * rowH;
      this.statLabels.push(
        this.add.text(x, y, k, {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '18px',
          color: '#9fb4d8',
        }),
      );
      this.statValues.push(
        this.add
          .text(x + 360, y, '0', {
            fontFamily: 'Trebuchet MS, sans-serif',
            fontSize: '18px',
            fontStyle: 'bold',
            color: '#ffffff',
          })
          .setOrigin(1, 0),
      );
    });
  }

  private drawStats(def: BeyDef): void {
    const x = WORLD.width / 2 - 180;
    const top = 462;
    const rowH = 26;
    const barX = x + 110;
    const barW = 210;
    const barH = 12;
    this.statsG.clear();
    STAT_KEYS.forEach((k, i) => {
      const v = def.stats[k];
      const y = top + i * rowH + 5;
      this.statsG.fillStyle(0x222a38, 1);
      this.statsG.fillRoundedRect(barX, y, barW, barH, 3);
      this.statsG.fillStyle(def.color, 1);
      this.statsG.fillRoundedRect(barX, y, (barW * v) / 10, barH, 3);
      this.statValues[i].setText(String(v));
    });
  }

  private move(dir: number): void {
    this.index = (this.index + dir + ROSTER.length) % ROSTER.length;
    sound.blip();
    this.refresh();
  }

  private cycleArena(): void {
    this.arenaIdx = (this.arenaIdx + 1) % ARENAS.length;
    this.registry.set('arena', ARENAS[this.arenaIdx].id);
    savePrefs({ arena: ARENAS[this.arenaIdx].id, difficultyIdx: this.difficultyIdx });
    sound.blip();
    this.refreshArena();
  }

  private refreshArena(): void {
    const a = ARENAS[this.arenaIdx];
    this.arenaLabel.setText(`Arena:  ${a.name} — ${a.blurb}`);
  }

  private cycleDifficulty(): void {
    this.difficultyIdx = (this.difficultyIdx + 1) % DIFFS.length;
    savePrefs({ arena: ARENAS[this.arenaIdx].id, difficultyIdx: this.difficultyIdx });
    sound.blip();
    this.refreshDifficulty();
  }

  private refreshDifficulty(): void {
    this.difficultyLabel.setText(`Difficulty:  ${DIFFS[this.difficultyIdx].name}  (D)`);
  }

  private refresh(): void {
    this.cards.forEach((card, i) => {
      const selected = i === this.index;
      card.setScale(selected ? 1.06 : 0.9);
      card.setAlpha(selected ? 1 : 0.55);
    });
    // Spin the selected face for flair.
    this.faces.forEach((f, i) => f.setAngle(i === this.index ? f.angle : 0));

    const def = ROSTER[this.index];
    this.drawStats(def);
    this.blurb.setText(def.blurb);
    const sp = specialFor(def.type);
    this.specialText.setText(`★ Special: ${sp.name}`);
  }

  update(_t: number, delta: number): void {
    const f = this.faces[this.index];
    if (f) f.angle += (delta / 1000) * 240;
  }

  private confirm(): void {
    const playerId = ROSTER[this.index].id;
    if (this.mode === 'versus') {
      if (this.versusPlayer === 1) {
        // Hand off to Player 2's pick.
        this.scene.start('Select', { mode: 'versus', versusPlayer: 2, p1Id: playerId });
      } else {
        this.scene.start('Battle', {
          playerId: this.p1Id,
          opponentId: playerId,
          difficulty: 0.7,
          mode: 'versus',
        });
      }
    } else if (this.mode === 'tournament') {
      this.registry.set('run', newRun(playerId, this.hard));
      this.scene.start('Tournament', {});
    } else {
      const opponent = pickOpponent(playerId);
      this.scene.start('Battle', {
        playerId,
        opponentId: opponent.id,
        difficulty: DIFFS[this.difficultyIdx].value,
        mode: 'exhibition',
      });
    }
  }
}
